import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import orderService from '../api/orderService';

export const useRecentOrders = () => {
    return useQuery({
        queryKey: ['recentOrders'],
        queryFn: () => orderService.recentOrders(),
        staleTime: 5 * 60 * 1000, // cache 5 mins
        refetchInterval: 60 * 1000 // refresh every 1 min
    });
};

export const useAllOrders = (filterParams) => {
    return useQuery({
        queryKey: ['allOrders', filterParams],
        queryFn: () => orderService.allOrders(filterParams),
        staleTime: 5 * 60 * 1000, // cache 5 mins
        refetchInterval: 2 * 60 * 1000 // refresh every 2 mins
    });
};

export const useAdminAllOrders = (filterParams) => {
    return useQuery({
        queryKey: ['allOrders', filterParams],
        queryFn: () => orderService.adminAllOrders(filterParams),
        staleTime: 5 * 60 * 1000, // cache 5 mins
        refetchInterval: 2 * 60 * 1000 // refresh every 2 mins
    });
};

export const useOrderDetails = (orderId) => {
    return useQuery({
        queryKey: ['orderDetails', orderId],
        queryFn: () => orderService.getOrderDetails(orderId),
        enabled: !!orderId, // only run this query if orderId is truthy
        staleTime: 5 * 60 * 1000, // cache 5 mins
        refetchInterval: 2 * 60 * 1000 // refresh every 2 mins
    });
};

export const useAdminOrderDetails = (orderId) => {
    return useQuery({
        queryKey: ['orderDetails', orderId],
        queryFn: () => orderService.adminGetOrderDetails(orderId),
        enabled: !!orderId,
        staleTime: 5 * 60 * 1000,
        refetchInterval: 2 * 60 * 1000
    });
};

export const useUpdateOrderStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // Destructure the object passed from the UI
        mutationFn: ({ orderId, ...payload }) => orderService.updateOrderStatus(orderId, payload),

        // This automatically refreshes the list after an update
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['allOrders'] });
            queryClient.invalidateQueries({ queryKey: ['orderDetails', variables.orderId] });
            queryClient.invalidateQueries({ queryKey: ['recentOrders'] });
        }
    });
};

export const useUpdateOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // Destructure the object passed from the UI
        mutationFn: ({ orderId, ...payload }) => orderService.updateOrderStatus(orderId, payload),

        // This automatically refreshes the list after an update
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['allOrders'] });
            queryClient.invalidateQueries({ queryKey: ['recentOrders'] });
        }
    });
};

export const useAdminUpdateOrderStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orderId, ...payload }) => orderService.adminUpdateOrderStatus(orderId, payload),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['allOrders'] });
            queryClient.invalidateQueries({ queryKey: ['orderDetails', variables.orderId] });
            queryClient.invalidateQueries({ queryKey: ['recentOrders'] });
        }
    });
};

export const useAvailableRiders = (orderId) => {
    return useQuery({
        queryKey: ['availableRiders', orderId],
        queryFn: () => orderService.getAvailableRiders(orderId),
        enabled: !!orderId,
        staleTime: 5 * 60 * 1000 // cache 5 mins
    });
};

export const useVendorAssignRider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orderId, riderId }) => orderService.vendorAssignRider(orderId, riderId),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['orderDetails', variables.orderId] });
            queryClient.invalidateQueries({ queryKey: ['allOrders'] });
        }
    });
};

export const useVendorNotifyRider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orderId }) => orderService.vendorNotifyRider(orderId),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['orderDetails', variables.orderId] });
            queryClient.invalidateQueries({ queryKey: ['allOrders'] });
        }
    });
};

export const useAdminRiderLocation = (orderId) => {
    return useQuery({
        queryKey: ['riderLocation', orderId],
        queryFn: () => orderService.adminGetRiderLocation(orderId),
        enabled: !!orderId,
        staleTime: 0,
        refetchInterval: 15 * 1000
    });
};

export const useVendorRiderLocation = (orderId) => {
    return useQuery({
        queryKey: ['riderLocation', orderId],
        queryFn: () => orderService.vendorGetRiderLocation(orderId),
        enabled: !!orderId,
        staleTime: 0,
        refetchInterval: 15 * 1000
    });
};
